import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { StarFill, ArrowLeftCircle, ListUl, XCircleFill, BoxArrowUpRight } from 'react-bootstrap-icons';
import { useNavigate } from 'react-router-dom';
import Modal from 'react-bootstrap/Modal';
import bestBuyData from '../data/bestBuyData.json';
import './BestBuy.css';

// Local image imports
import netflixLogo from '../assets/images/netflix_logo.jpg';
import primeVideoLogo from '../assets/images/prime_video_logo.jpg';
import hotstarLogo from '../assets/images/hotstar_logo.jpg';

// Map local: prefixed keys to imported images
const localImages = {
  'netflix_logo': netflixLogo,
  'prime_video_logo': primeVideoLogo,
  'hotstar_logo': hotstarLogo,
};

const resolveImage = (imageUrl) => {
  if (imageUrl && imageUrl.startsWith('local:')) {
    const key = imageUrl.replace('local:', '');
    return localImages[key] || '';
  }
  return imageUrl;
};

const BestBuy = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('OTT');
  const [comingSoonCategory, setComingSoonCategory] = useState(null);
  const [showListModal, setShowListModal] = useState(false);
  const [activeItem, setActiveItem] = useState(null);

  const categories = bestBuyData.categories;
  const activeCategory = categories.find(cat => cat.id === selectedCategory);
  const items = activeCategory ? activeCategory.items : [];
  const isMediaCategory = activeCategory?.type === 'media' || selectedCategory === 'OTT';

  const handleShowList = (item) => {
    setActiveItem(item);
    setShowListModal(true);
  };

  return (
    <div className="bestbuy-container">
      <div className="container py-5" style={{ maxWidth: '1200px' }}>

        {/* Header Section */}
        <div className="bestbuy-header d-flex justify-content-between align-items-center mb-5">
          <div>
            <h2 className="brand-text fw-bold text-dark m-0">
              BestBuy <span className="text-primary">Curated</span>
            </h2>
            <p className="text-muted small m-0 mt-1">
              Handpicked top-rated products & services with verified recommendations
            </p>
          </div>
          <button
            className="btn btn-outline-dark btn-sm rounded-pill px-3"
            onClick={() => navigate('/utility-dashboard')}
          >
            <ArrowLeftCircle className="me-1" size={16} /> Back to Dashboard
          </button>
        </div>

        {/* Category Tabs */}
        <div className="d-flex gap-2 mb-5 justify-content-center flex-wrap">
          {categories.map(cat => (
            <button
              key={cat.id}
              className={`btn-category ${selectedCategory === cat.id ? 'active' : ''}`}
              onClick={() => {
                if (cat.id === 'OTT') {
                  setSelectedCategory('OTT');
                  setComingSoonCategory(null);
                  return;
                }

                setComingSoonCategory(cat.id);
              }}
            >
              <i className={`${cat.icon} me-2`}></i>
              {cat.label}
            </button>
          ))}
        </div>

        {/* Product Grid */}
        <div className="row g-4 justify-content-center">
          {comingSoonCategory ? (
            <motion.div
              key={comingSoonCategory}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="col-12 text-center py-5"
            >
              <h5 className="fw-light text-secondary">Coming soon..</h5>
            </motion.div>
          ) : (
            <AnimatePresence mode="wait">
              {items.map((item, index) => {
                const isLogoItem = item.imageUrl?.startsWith('local:') || isMediaCategory;
                return (
                  <motion.div
                    key={item.id}
                    className="col-12 col-md-6 col-lg-4"
                    layout
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.35, delay: index * 0.08 }}
                  >
                    <div className="card h-100 glass-card-product border-0 shadow-sm overflow-hidden">
                      {/* Image */}
                      <div
                        className={`product-image-wrapper ${isLogoItem ? 'bestbuy-logo-wrapper' : 'bestbuy-photo-wrapper'}`}
                        style={{ position: 'relative', height: '240px' }}
                      >
                        <img
                          src={resolveImage(item.imageUrl)}
                          className={isLogoItem ? 'bestbuy-logo-img' : 'bestbuy-photo-img'}
                          alt={item.name}
                          onError={(e) => {
                            e.target.src = 'https://images.unsplash.com/photo-1594909122845-11baa439b7bf?q=80&w=500';
                          }}
                        />
                        {/* Rating Badge */}
                        <div className="rating-badge-glass position-absolute top-0 end-0 m-2">
                          <StarFill className="text-warning me-1" size={12} /> {item.rating}
                        </div>
                        {/* Tag Badge */}
                        {item.tag && (
                          <div className="bestbuy-tag-badge position-absolute top-0 start-0 m-2">
                            {item.tag}
                          </div>
                        )}
                      </div>

                      {/* Card Body */}
                      <div className="card-body d-flex flex-column p-4">
                        <h6 className="card-title mb-2 fw-bold text-dark">{item.name}</h6>
                        <p className="card-text small text-secondary flex-grow-1 mb-4" style={{ lineHeight: '1.5' }}>
                          {item.description}
                        </p>

                        <div className="d-flex gap-2 mt-auto">
                          <button
                            className="btn btn-action-pill flex-grow-1"
                            onClick={() => handleShowList(item)}
                          >
                            <ListUl className="me-1" size={18} />
                            Visit best list
                          </button>
                          {item.link && (
                            <a
                              href={item.link}
                              target="_blank"
                              rel="noreferrer"
                              className="btn btn-outline-secondary btn-icon-pill"
                              title="Visit Official / Store Page"
                            >
                              <BoxArrowUpRight size={16} />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          )}

          {/* Empty State */}
          {!comingSoonCategory && items.length === 0 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="col-12 text-center py-5">
              <h5 className="fw-light text-secondary">
                No recommendations available for this category yet.
              </h5>
              <p className="text-muted small">Check back soon — we're adding more every day!</p>
            </motion.div>
          )}
        </div>

        {/* Disclaimer */}
        <div className="bestbuy-disclaimer">
          <p>
            <strong>Disclaimer:</strong> TrustApp is not affiliated with, endorsed by, or sponsored by any of the platforms, brands, or services listed on this page. All product names, logos, and trademarks are the property of their respective owners. The recommendations shown here are purely informational and curated for user convenience. There is no monetization or commercial intent behind these listings.
          </p>
        </div>
      </div>

      {/* Best List Modal */}
      <Modal
        show={showListModal}
        onHide={() => setShowListModal(false)}
        centered
        size="lg"
        dialogClassName="bestlist-modal-dialog"
        contentClassName="bestlist-modal-content"
      >
        <Modal.Header className="bestlist-modal-header">
          <div>
            <Modal.Title className="bestlist-modal-title">
              {activeItem?.name} — Best Picks
            </Modal.Title>
            <div className="text-white-50 small mt-1">
              Top curated recommendations with ratings & verified links
            </div>
          </div>
          <button className="bestlist-close-btn" onClick={() => setShowListModal(false)}>
            <XCircleFill size={22} />
          </button>
        </Modal.Header>
        <Modal.Body className="bestlist-modal-body">
          {activeItem?.bestList && activeItem.bestList.length > 0 ? (
            <div className="table-responsive">
              <table className="table bestlist-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{isMediaCategory ? 'Title' : 'Model / Item Name'}</th>
                    <th>{isMediaCategory ? 'Category' : 'Specification / Type'}</th>
                    <th>Rating</th>
                  </tr>
                </thead>
                <tbody>
                  {activeItem.bestList.map((entry, idx) => (
                    <tr key={idx}>
                      <td className="bestlist-index">{idx + 1}</td>
                      <td className="bestlist-name">
                        <a href={entry.link} target="_blank" rel="noreferrer" className="bestlist-link">
                          {entry.title} <BoxArrowUpRight className="ms-1 opacity-50" size={12} />
                        </a>
                      </td>
                      <td>
                        <span className={`bestlist-type-badge ${entry.type === 'Movie' ? 'type-movie' : 'type-series'}`}>
                          {entry.type}
                        </span>
                      </td>
                      <td className="bestlist-imdb">
                        <StarFill className="text-warning me-1" size={12} />
                        {entry.score || entry.imdb || '4.5/5'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center text-muted py-4">No recommendations added yet.</p>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default BestBuy;